import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, IconButton, TextField, List, ListItemButton, ListItemText, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import { getTemplates, saveTemplate, deleteTemplate } from '../utils/taskTemplates';
import { showSuccess, showError } from '../utils/toast';
import { useThemeMode } from '../theme/ThemeContext';

const PRIORITY_LABELS = { 1: 'Standard', 2: 'Important', 3: 'Urgent', 4: 'Important+Urgent' };

/**
 * Template manager dialog. Listens to:
 *  - 'openTaskTemplates' event with optional detail { mode: 'save'|'pick', task?: {...} }
 *    - 'save' shows a form to save the provided task as template
 *    - 'pick' (default) shows a list for the user to apply a template
 *
 * Dispatches:
 *  - 'taskTemplateSelected' with detail: { template } when user picks one
 */
const TaskTemplatesDialog = () => {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('pick');
    const [templates, setTemplates] = useState([]);
    const [prefillTask, setPrefillTask] = useState(null);
    const [newName, setNewName] = useState('');
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const { themeMode } = useThemeMode();
    const isDark = themeMode === 'dark';

    useEffect(() => {
        const handler = (e) => {
            const m = e.detail?.mode || 'pick';
            setMode(m);
            setPrefillTask(e.detail?.task || null);
            setNewName('');
            setTemplates(getTemplates());
            setOpen(true);
        };
        window.addEventListener('openTaskTemplates', handler);
        return () => window.removeEventListener('openTaskTemplates', handler);
    }, []);

    useEffect(() => {
        const handler = () => setTemplates(getTemplates());
        window.addEventListener('taskTemplatesChanged', handler);
        return () => window.removeEventListener('taskTemplatesChanged', handler);
    }, []);

    const handlePick = (template) => {
        window.dispatchEvent(new CustomEvent('taskTemplateSelected', { detail: { template } }));
        setOpen(false);
    };

    const handleDelete = (template) => {
        setTemplateToDelete(template);
    };

    const confirmDelete = () => {
        if (!templateToDelete) return;
        try {
            deleteTemplate(templateToDelete.id);
            showSuccess('Template deleted');
        } catch (err) {
            showError(err.message || 'Failed to delete template');
        } finally {
            setTemplateToDelete(null);
        }
    };

    const handleSave = () => {
        if (!newName.trim()) {
            showError('Template name is required');
            return;
        }
        try {
            saveTemplate({
                name: newName,
                title: prefillTask?.title || '',
                description: prefillTask?.description || '',
                priority: prefillTask?.priority || 1,
                types: prefillTask?.types || [],
                estimated: prefillTask?.estimated || 0,
                notification_data: prefillTask?.notification_data || { id: 11 }
            });
            showSuccess('Template saved');
            setOpen(false);
        } catch (err) {
            showError(err.message || 'Failed to save template');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', background: isDark ? '#1e1e1e' : '#fff' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '16px', color: isDark ? '#e0e0e0' : '#1f2d3d' }}>
                    {mode === 'save' ? 'Save as Template' : 'Task Templates'}
                </Typography>
                <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: isDark ? '#ccc' : '#666' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                {mode === 'save' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666', fontSize: '13px' }}>
                            Save this task's title, description, priority and effort as a reusable template.
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            label="Template name"
                            placeholder="e.g. Daily standup"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                        />
                        {prefillTask && (
                            <Box sx={{
                                p: 1.5,
                                borderRadius: '8px',
                                backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5',
                                fontSize: '12px'
                            }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: isDark ? '#ccc' : '#555', mb: 0.5 }}>
                                    Preview:
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: isDark ? '#e0e0e0' : '#1f2d3d' }}>
                                    {prefillTask.title || '(untitled)'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box>
                        {templates.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <DescriptionIcon sx={{ fontSize: 40, color: isDark ? '#555' : '#ccc', mb: 1 }} />
                                <Typography variant="body2" sx={{ color: isDark ? '#888' : '#999', fontSize: '13px' }}>
                                    No templates yet
                                </Typography>
                                <Typography variant="caption" sx={{ color: isDark ? '#666' : '#bbb', fontSize: '11px', display: 'block', mt: 0.5 }}>
                                    Save a task as template to reuse it later
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ py: 0 }}>
                                {templates.map((t) => (
                                    <ListItemButton
                                        key={t.id}
                                        onClick={() => handlePick(t)}
                                        sx={{
                                            borderRadius: '8px',
                                            mb: 0.5,
                                            '&:hover': { backgroundColor: isDark ? 'rgba(39,174,134,0.1)' : 'rgba(39,174,134,0.05)' }
                                        }}
                                    >
                                        <ListItemText
                                            primary={t.name}
                                            secondary={t.title || '(no default title)'}
                                            primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 600, color: isDark ? '#e0e0e0' : '#1f2d3d' } }}
                                            secondaryTypographyProps={{ sx: { fontSize: '12px', color: isDark ? '#aaa' : '#777' } }}
                                        />
                                        <Chip
                                            label={PRIORITY_LABELS[t.priority] || 'Standard'}
                                            size="small"
                                            sx={{ height: 20, fontSize: '10px', mr: 1 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleDelete(t); }}
                                            sx={{ color: isDark ? '#888' : '#999', '&:hover': { color: '#d32f2f' } }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </Box>
                )}
            </DialogContent>
            {mode === 'save' && (
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: isDark ? '#aaa' : '#666' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!newName.trim()}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#27ae86',
                            '&:hover': { backgroundColor: '#1f8f6d' }
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            )}
            <Dialog
                open={Boolean(templateToDelete)}
                onClose={() => setTemplateToDelete(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px', background: isDark ? '#1e1e1e' : '#fff' } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '16px', color: isDark ? '#e0e0e0' : '#1f2d3d', pb: 1 }}>
                    Delete template?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '13px', color: isDark ? '#aaa' : '#555' }}>
                        This will permanently remove
                        {' '}
                        <Box component="span" sx={{ fontWeight: 700, color: isDark ? '#e0e0e0' : '#1f2d3d' }}>
                            {templateToDelete?.name || 'this template'}
                        </Box>
                        . This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setTemplateToDelete(null)}
                        sx={{ textTransform: 'none', color: isDark ? '#aaa' : '#666' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#d32f2f',
                            '&:hover': { backgroundColor: '#b71c1c' }
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default TaskTemplatesDialog;
